import { useEffect, useState } from "react";
import { Instance } from "./pages/Instance";
import { LoginGate } from "./components/LoginGate";
import { PhaseReview } from "./components/phase-review/PhaseReview";
import { PhaseReviewIndex } from "./components/phase-review/PhaseReviewIndex";
import { applySkin, hostLockedInstance } from "./skins";


/**
 * Tiny path-based router: /<instance>[/<sid>] → instance app (chat shell),
 * gated by LoginGate. Every other path, including the bare root, is a
 * featureless 404.
 */

interface Route {
  kind: "instance" | "phase-review" | "phase-review-index" | "notfound";
  instance?: string;
  sid?: string;
  /** phase-review only: which client's page. */
  sample?: string;
}

function parseRoute(pathname: string): Route {
  const parts = pathname.replace(/^\/|\/$/g, "").split("/").filter(Boolean);
  // Whitelabel hostname → the app IS that instance: `/` is its chat and the
  // first path segment is a session id, never an instance slug.
  const locked = hostLockedInstance();
  if (locked) return { kind: "instance", instance: locked, sid: parts[0] };
  // The bare root is hidden: no landing, no sign-in, no instance list. A tenant
  // reaches their workspace only via its own /<instance> URL.
  if (parts.length === 0) return { kind: "notfound" };
  const [instance, sid, third] = parts;
  // /<instance>/phase-review/<client> — the read-only suggestion review pages,
  // and /<instance>/phase-review — the picker that opens one.
  if (sid === "phase-review") {
    return third
      ? { kind: "phase-review", instance, sample: third }
      : { kind: "phase-review-index", instance };
  }
  return { kind: "instance", instance, sid };
}

// Deliberately featureless not-found: the entry point must reveal nothing about
// the platform — no branding, no sign-in, no hint that this is multi-tenant.
function NotFound() {
  return (
    <div style={{ font: "14px system-ui, sans-serif", color: "#666", textAlign: "center", marginTop: "20vh" }}>
      404 — Not found
    </div>
  );
}


export function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Whitelabel: stamp the instance's skin (palette + title) on the document.
  useEffect(() => {
    applySkin(route.kind === "instance" ? route.instance : null);
  }, [route]);

  const navigate = (to: string) => {
    // On a whitelabel hostname the components still build /<instance>/<sid>
    // paths — strip the slug so the address bar stays clean.
    const locked = hostLockedInstance();
    if (locked && (to === `/${locked}` || to.startsWith(`/${locked}/`))) {
      to = to.slice(locked.length + 1) || "/";
    }
    if (to !== window.location.pathname) {
      window.history.pushState({}, "", to);
      setRoute(parseRoute(to));
    }
  };

  // Bare root (and any non-instance path) → 404, BEFORE the login gate, so the
  // entry point shows no sign-in screen and nothing that hints at the platform.
  if (route.kind === "notfound") return <NotFound />;

  // Phase-suggestion review — gated the same way, but its own surface: no chat
  // shell, no session list, nothing that could write to the instance.
  if (route.kind === "phase-review") {
    return (
      <LoginGate>
        {(me) => (
          <PhaseReview instance={route.instance!} me={me} sampleId={route.sample!} />
        )}
      </LoginGate>
    );
  }

  // The picker that opens one of those pages — same gate, same surface.
  if (route.kind === "phase-review-index") {
    return (
      <LoginGate>
        {(me) => <PhaseReviewIndex instance={route.instance!} me={me} />}
      </LoginGate>
    );
  }

  // Instance app — gated; the sign-in screen only ever appears in the context
  // of a specific /<instance> URL.
  return (
    <LoginGate>
      {(me) => <Instance instance={route.instance!} sid={route.sid} onNavigate={navigate} me={me} />}
    </LoginGate>
  );
}
